// src/admin/components/ProductForm.jsx
import React, { useState, useEffect } from "react";
import { adminCreateProduct, adminListCategories, adminListSubCategories, adminUpdateProduct } from "../api/admin";
import "./ProductForm.css";
import ImageUpload from "./ImageUpload";
import RichTextEditor from "./RichTextEditor";
import { toast } from "react-toastify";

const ProductForm = ({ product, onSuccess, onCancel }) => {
  // ===== State for all backend fields =====
  const [name, setName] = useState("");
  const [shortDesc, setShortDesc] = useState("");
  const [detailedDesc, setDetailedDesc] = useState("");
  const [category, setCategory] = useState("");
  const [subCategory, setSubCategory] = useState("");
  const [url, setUrl] = useState("");
  const [type, setType] = useState("product");
  const [freeDelivery, setFreeDelivery] = useState(false);
  const [metaTitle, setMetaTitle] = useState("");
  const [metaDescription, setMetaDescription] = useState("");
  const [metaKeywords, setMetaKeywords] = useState("");
  const [schemaMarkup, setSchemaMarkup] = useState("");
  const [images, setImages] = useState([]); // items can be { url: string, file: File | null }
  const [is50Voucher, setIs50Voucher] = useState(false);
  const [is100Voucher, setIs100Voucher] = useState(false);
  const [deletedImageKeys, setDeletedImageKeys] = useState([]); // Track keys of existing images removed during edit
  const [categories, setCategories] = useState([]);
  const [allSubCategories, setAllSubCategories] = useState([]);
  const [filteredSubCategories, setFilteredSubCategories] = useState([]);

  // ✅ Define once, reusable everywhere
  const resetForm = () => {
    setName("");
    setShortDesc("");
    setDetailedDesc("");
    setCategory("");
    setSubCategory("");
    setUrl("");
    setType("product");
    setFreeDelivery(false);
    setMetaTitle("");
    setMetaDescription("");
    setMetaKeywords("");
    setSchemaMarkup("");
    setImages([]);
    setIs50Voucher(false);
    setIs100Voucher(false);
    setIsReturnAvailable(false);
    setReturnDays(0);
    setDeletedImageKeys([]);
  };

  // State
  const [isReturnAvailable, setIsReturnAvailable] = useState(false);
  const [returnDays, setReturnDays] = useState(0);

  // ===== Populate form if editing =====
  useEffect(() => {
    if (product) {
      setName(product.p_name || "");
      setShortDesc(product.p_short_description || "");
      setDetailedDesc(product.p_detailed_description || "");
      setCategory(product.p_category || "");
      setSubCategory(product.p_sub_category || "");
      setUrl(product.p_url || "");
      setType(product.p_Product_Type || "product");
      setFreeDelivery(product.p_free_delivery || false);
      setMetaTitle(product.p_meta_Title || "");
      setMetaDescription(product.p_meta_Description || "");
      setSchemaMarkup(product.p_schema_Markup || "");
      setIs50Voucher(product.p_is_50_voucher || false);
      setIs100Voucher(product.p_is_100_voucher || false);
      setIsReturnAvailable(product.isReturnAvailable || false);
      setReturnDays(product.returnDays || 0);
      setMetaKeywords(
        Array.isArray(product.p_meta_Keywords)
          ? product.p_meta_Keywords.join(",")
          : product.p_meta_Keywords || ""
      );

      // Image population
      const productImages = product.p_images || [];
      if (Array.isArray(productImages)) {
        setImages(productImages.map(img => ({ 
          url: typeof img === 'string' ? img : (img.url || ""), 
          file: null,
          key: typeof img === 'string' ? null : img.key
        })));
      } else {
        setImages([]);
      }
      setDeletedImageKeys([]);
    } else {
      resetForm();
    }
  }, [product]);

  const handleAddImages = (files) => {
    const MAX_SIZE = 20 * 1024 * 1024; // 20MB
    const validFiles = [];
    let oversizedCount = 0;

    files.forEach(file => {
      if (file.size > MAX_SIZE) {
        oversizedCount++;
      } else {
        validFiles.push(file);
      }
    });

    if (oversizedCount > 0) {
      toast.warn(`${oversizedCount} image(s) exceeded the 20MB limit and were not added.`);
    }

    const newImages = validFiles.map(file => ({
      url: URL.createObjectURL(file),
      file: file
    }));
    setImages(prev => [...prev, ...newImages]);
  };

  const removeImage = (index) => {
    setImages(prev => {
      const updated = [...prev];
      const imageToRemove = updated[index];

      // If it's an existing image (no file object) and has a key, track it for deletion
      if (!imageToRemove.file) {
        // Handle both {url, key} object and simple string URL
        const key = typeof imageToRemove.url === 'object' ? imageToRemove.url?.key : null;
        if (key) {
          setDeletedImageKeys(prev => [...prev, key]);
        }
      }

      // If it's a new upload (has file object), revoke the preview URL
      if (imageToRemove.file) {
        URL.revokeObjectURL(imageToRemove.url);
      }

      updated.splice(index, 1);
      return updated;
    });
  };

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        const [catRes, subRes] = await Promise.all([
          adminListCategories(),
          adminListSubCategories()
        ]);
        setCategories(catRes.data || []);
        setAllSubCategories(subRes.data || []);
      } catch (err) {
        console.error("Failed to fetch initial data", err);
      }
    };
    fetchInitialData();
  }, []);

  // Filter subcategories when category changes
  useEffect(() => {
    if (category && categories.length > 0) {
      const selectedCat = categories.find(c => c.category_name === category);
      if (selectedCat) {
        // Use all possible ID fields for stability
        const catId = selectedCat.category_id || selectedCat._id || selectedCat.id;
        const filtered = allSubCategories.filter(s =>
          (s.category_id || s.parent_category_id) === catId
        );
        setFilteredSubCategories(filtered);

        // Reset subCategory selection if it's no longer valid
        if (subCategory && !filtered.some(s => (s.sub_category_name || s.subcategory_name) === subCategory)) {
          setSubCategory("");
        }
      } else {
        setFilteredSubCategories([]);
        setSubCategory("");
      }
    } else {
      setFilteredSubCategories([]);
      setSubCategory("");
    }
  }, [category, categories, allSubCategories, subCategory]);

  // ===== Handle form submit =====
  const handleSubmit = async (e) => {
    e.preventDefault();

    const formData = new FormData();
    formData.append("p_name", name);
    formData.append("p_short_description", shortDesc);
    formData.append("p_detailed_description", detailedDesc);
    formData.append("p_category", category);
    formData.append("p_sub_category", subCategory);
    formData.append("p_url", url);
    formData.append("p_Product_Type", type);
    formData.append("p_free_delivery", freeDelivery);
    formData.append("p_is_50_voucher", is50Voucher);
    formData.append("p_is_100_voucher", is100Voucher);
    formData.append("p_meta_Title", metaTitle);
    formData.append("p_meta_Description", metaDescription);
    formData.append("p_schema_Markup", schemaMarkup);
    formData.append("isReturnAvailable", isReturnAvailable);
    formData.append("returnDays", returnDays);

    // Keywords - Send as a single string as per Swagger docs to avoid nesting issues
    formData.append("p_meta_Keywords", metaKeywords);

    // 1. Existing images (to keep)
    const existingImagesPayload = [];
    images.forEach(img => {
      if (!img.file) {
        // Robust extraction of url and key
        let url = "";
        let key = "";

        if (typeof img.url === 'object' && img.url !== null) {
          url = img.url.url || "";
          key = img.url.key || "";
        } else {
          url = img.url || "";
        }

        if (url) {
          existingImagesPayload.push({ url, key });
        }
      }
    });
    formData.append("existing_images", JSON.stringify(existingImagesPayload));

    // 2. Deleted images (keys only)
    formData.append("delete_image_keys", JSON.stringify(deletedImageKeys));

    // 3. New Files: Only file objects go to "images" to satisfy Pydantic UploadFile validation
    images.forEach((img) => {
      if (img.file) {
        formData.append("images", img.file);
      }
    });

    // Debugging payload before API call
    console.log("=== Form Data Payload Debug ===");
    for (let pair of formData.entries()) {
      console.log(`${pair[0]}:`, pair[1]);
    }

    try {
      if (product?.id || product?._id) {
        const productId = product._id || product.id;
        await adminUpdateProduct(productId, formData);
        toast.success("Product updated successfully! ✨");
      } else {
        await adminCreateProduct(formData);
        toast.success("Product created successfully! 🎉");
      }
      resetForm();
      onSuccess(); // refresh table
    } catch (err) {
      console.error("Error saving product:", err);
      toast.error(err.response?.data?.message || "Failed to save product. ❌");
    }
  };

  // ===== Auto-resize for textareas =====
  const handleAutoResize = (e) => {
    const target = e.target || e;
    target.style.height = "auto";
    target.style.height = `${target.scrollHeight}px`;
  };

  // Trigger resize on load for existing content
  useEffect(() => {
    const timer = setTimeout(() => {
      const textareas = document.querySelectorAll('.admin-form textarea');
      textareas.forEach(ta => handleAutoResize(ta));
    }, 100);
    return () => clearTimeout(timer);
  }, [product]);

  return (
    <form onSubmit={handleSubmit} className="admin-form">
      <div className="form-body">
        {/* Section 1: Basic Info */}
        <div className="form-section-title">📦 Basic Information</div>
        <div className="form-grid">
          <div className="form-group">
            <label>Product Name <span className="required-star">*</span></label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter product name"
              required
            />
          </div>

          <div className="form-group">
            <label>Product Url <span className="required-star">*</span></label>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="Enter Product URL" required />
          </div>

          <div className="form-group">
            <label>Category <span className="required-star">*</span></label>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              required
            >
              <option value="">-- Select Category --</option>
              {categories.map((c) => (
                <option key={c.id || c._id} value={c.category_name}>
                  {c.category_name}
                </option>
              ))}
            </select>
          </div>

          {filteredSubCategories.length > 0 && (
            <div className="form-group">
              <label>Sub Category</label>
              <select
                value={subCategory}
                onChange={(e) => setSubCategory(e.target.value)}
              >
                <option value="">-- Select Sub Category --</option>
                {filteredSubCategories.map((s) => (
                  <option key={s.id || s._id || s.subcategory_id} value={s.sub_category_name || s.subcategory_name}>
                    {s.sub_category_name || s.subcategory_name}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="form-group">
            <label>Product Type</label>
            <select value={type} onChange={(e) => setType(e.target.value)}>
              <option value="product">Product</option>
              <option value="combo">Combo</option>
            </select>
          </div>
        </div>

        {/* Section 2: Descriptions */}
        <div className="form-section-title">📝 Descriptions</div>
        <div className="form-group full-width">
          <label>Short Description</label>
          <textarea
            value={shortDesc}
            onChange={(e) => {
              setShortDesc(e.target.value);
              handleAutoResize(e);
            }}
            onInput={handleAutoResize}
            placeholder="Brief summary of the product"
            rows={1}
            style={{ minHeight: '45px', overflowY: 'hidden' }}
          />
        </div>

        <div className="form-group full-width">
          <label>Detailed Description</label>
          <RichTextEditor
            value={detailedDesc}
            onChange={setDetailedDesc}
            placeholder="Full detailed description..."
          />
        </div>

        {/* Section 3: SEO & Metadata */}
        <div className="form-section-title">🔍 SEO & Metadata</div>
        <div className="form-grid">
          <div className="form-group full-width">
            <label>Meta Title</label>
            <input value={metaTitle} onChange={(e) => setMetaTitle(e.target.value)} placeholder="SEO Title" />
          </div>
        </div>

        <div className="form-group full-width">
          <label>Meta Description</label>
          <textarea
            value={metaDescription}
            onChange={(e) => {
              setMetaDescription(e.target.value);
              handleAutoResize(e);
            }}
            onInput={handleAutoResize}
            placeholder="SEO description for search engines"
            style={{ minHeight: '80px', overflowY: 'hidden' }}
          />
        </div>

        <div className="form-group full-width">
          <label>Meta Keywords (comma separated)</label>
          <input
            value={metaKeywords}
            onChange={(e) => setMetaKeywords(e.target.value)}
            placeholder="e.g., rice, organic, healthy"
          />
        </div>

        <div className="form-grid">
          <div className="form-group full-width">
            <label>Schema Markup</label>
            <textarea
              value={schemaMarkup}
              onChange={(e) => {
                setSchemaMarkup(e.target.value);
                handleAutoResize(e);
              }}
              onInput={handleAutoResize}
              placeholder="JSON Schema"
              style={{ minHeight: '60px', overflowY: 'hidden' }}
            />
          </div>
        </div>

        {/* Section 4: Images */}
        <div className="form-section-title">🖼️ Product Images</div>
        <div className="form-group full-width">
          <ImageUpload
            images={images}
            onAdd={handleAddImages}
            onRemove={removeImage}
            multiple={true}
            id="product-image-upload"
          />
        </div>

        {/* Section 5: Extra Options */}
        <div className="form-section-title">⚙️ Extra Options</div>
        <div className="offer-options grid-3">
          <label className="checkbox-card">
            <input
              type="checkbox"
              checked={freeDelivery}
              onChange={(e) => setFreeDelivery(e.target.checked)}
            />
            <span>Free Delivery</span>
          </label>
          <label className="checkbox-card">
            <input
              type="checkbox"
              checked={is50Voucher}
              onChange={(e) => setIs50Voucher(e.target.checked)}
            />
            <span>50 Rs Voucher</span>
          </label>
          <label className="checkbox-card">
            <input
              type="checkbox"
              checked={is100Voucher}
              onChange={(e) => setIs100Voucher(e.target.checked)}
            />
            <span>100 Rs Voucher</span>
          </label>
        </div>

        <div className="form-section-title">📦 Return Policy</div>
        <div className="offer-options grid-2">
          <label className="checkbox-card">
            <input
              type="checkbox"
              checked={isReturnAvailable}
              onChange={(e) => setIsReturnAvailable(e.target.checked)}
            />
            <span>Return Available</span>
          </label>
          {isReturnAvailable && (
            <div className="form-group">
              <label>Return Days</label>
              <input
                type="number"
                value={returnDays}
                onChange={(e) => setReturnDays(e.target.value)}
                placeholder="e.g., 7"
                min="0"
              />
            </div>
          )}
        </div>

        <div className="form-footer">
          <button type="submit" className="btn-submit">
            {product ? "Update Product" : "Save Product"}
          </button>
          <button type="button" className="btn-cancel" onClick={() => { resetForm(); onCancel(); }}>
            Cancel
          </button>
        </div>
      </div>
    </form>
  );
};

export default ProductForm;
